/**
 * THE NOTEBOOK — the short column beside the lead. D-EZ-70.
 *
 * Howard, 20 Aug 2026, on the after-race article: *"is there anything we can do
 * to amke them more unique, more varied but retain their awesomeness?"*
 *
 * ===========================================================================
 * A PAGE IS MORE VARIED THAN AN ARTICLE, WHATEVER THE ARTICLE DOES
 * ===========================================================================
 *
 * The lead piece is one shape by necessity — it has a beginning and an end and
 * it is about one thing. A real racing weekly does not put one article on the
 * page; it puts an article and then a column of short items, and the items are
 * where the week's odds and ends go. Two editions with similar leads still read
 * differently when the notebook beside them is full of different names.
 *
 * ⚠ AND IT IS THE FACTS THE LEAD LEAVES ON THE FLOOR. The brief tells the writer
 * to use the incidents that make an afternoon make sense and leave the rest out
 * — which is right, and which means a man who failed to qualify, a shop that put
 * three cars in the top ten, or a driver who moved eleven places in the
 * standings simply never appears anywhere. This is where those go.
 *
 * ⚠ NO MODEL, EVER. Every item below is a sentence assembled from a number that
 * is already in `PaperFacts`, so the notebook is identical with a key and
 * without one. It is not a cheaper version of the article; it is a different
 * part of the page, and the part that cannot be wrong.
 */

import type { PaperFacts } from './paperFacts.ts';

export interface NoteItem {
  /** Two or three words, in the register of a standing column head. */
  head: string;
  /** One sentence. Never two. */
  text: string;
}

/** Newspapers put surnames in a notes column. */
function surname(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 ? (parts[parts.length - 1] as string) : name;
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

function list(items: readonly string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0] as string;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

function dollars(mb: number): string {
  const d = Math.round(mb * 10_000);
  if (d >= 1_000_000) return `$${(d / 1_000_000).toFixed(2).replace(/\.00$/, '')}M`;
  if (d >= 1_000) return `$${Math.round(d / 1_000)}K`;
  return `$${d.toLocaleString('en-US')}`;
}

/**
 * The column, longest-lived items first.
 *
 * ⚠ EVERY ITEM IS CONDITIONAL AND NONE IS PADDED. A quiet week produces a short
 * notebook, and a short notebook is the honest report of a quiet week. Inventing
 * a filler item — "the championship continues" — would be the column saying
 * something in order to have said something, which is how a feature like this
 * becomes wallpaper.
 */
export function notebookItems(facts: PaperFacts): NoteItem[] {
  const items: NoteItem[] = [];
  const race = facts.race;

  if (race) {
    // Who did not make the show. Never in the lead piece, always of interest.
    if (race.failedToQualify.length > 0) {
      items.push({
        head: 'Missed the show',
        text: `${list(race.failedToQualify.map(surname))} failed to qualify.`,
      });
    }

    if (race.absent.length > 0) {
      items.push({
        head: 'Not entered',
        text: `${list(race.absent.map((a) => `${surname(a.name)} (${a.reason})`))}.`,
      });
    }

    // The garage's afternoon, which no other part of the paper reports.
    const outByReason = new Map<string, string[]>();
    for (const f of race.fellOut) {
      const bucket = outByReason.get(f.reason) ?? [];
      bucket.push(surname(f.name));
      outByReason.set(f.reason, bucket);
    }
    /*
      ⚠ ONLY WHEN A REASON TOOK MORE THAN ONE CAR. A single retirement is the
      lead article's material and repeating it here is the page saying the same
      thing twice; a reason that took three is a pattern the article did not have
      room for.

      ⚠ AND ONE ITEM, NOT ONE PER REASON. Found in a screenshot: a week with two
      out on feuds and two with problems printed "In the garage" twice, in two
      different columns, which reads as the column repeating itself.
    */
    const garage = [...outByReason]
      .filter(([, who]) => who.length >= 2)
      .map(([reason, who]) => `${plural(who.length, 'car')} out with ${reason} (${list(who)})`);
    if (garage.length > 0) items.push({ head: 'In the garage', text: `${list(garage)}.` });

    if (race.pole && race.pole.name !== race.winner.name) {
      items.push({ head: 'From the pole', text: `${race.pole.name} started first for ${race.pole.team}.` });
    }

    if (race.biggestMover && race.biggestMover.gained > 0) {
      const m = race.biggestMover;
      items.push({
        head: 'Hard charger',
        text: `${m.name} started ${m.started} and finished ${m.finished}, a gain of ${m.gained}.`,
      });
    }
  }

  // The standings under the top five — the places nobody writes about.
  if (facts.championship.notableMoves.length > 0) {
    const moves = facts.championship.notableMoves
      .slice(0, 3)
      .map((m) => `${surname(m.name)} ${m.moved > 0 ? 'up' : 'down'} ${Math.abs(m.moved)} to ${m.rank}th`);
    items.push({ head: 'On the move', text: `${list(moves)}.` });
  }

  const s = facts.seasonSoFar;
  if (s.winlessInTopFive.length > 0) {
    items.push({
      head: 'Still winless',
      text: `${list(s.winlessInTopFive.map(surname))} ${
        s.winlessInTopFive.length === 1 ? 'is' : 'are'
      } inside the top five without a win.`,
    });
  }

  if (s.differentWinners > 1) {
    items.push({
      head: 'The year so far',
      text: `${plural(s.differentWinners, 'different winner')} in ${plural(facts.racesRun, 'race')}.`,
    });
  }

  if (s.mostTurnsLed) {
    items.push({
      head: 'Out front',
      text: `${s.mostTurnsLed.name} has led ${plural(s.mostTurnsLed.turns, 'turn')} this season, more than anybody.`,
    });
  }

  if (s.biggestEarner) {
    items.push({
      head: 'On the money',
      text: `${s.biggestEarner.name} has won ${dollars(s.biggestEarner.winningsMb)} on the racetrack this year.`,
    });
  }

  if (s.bestAverageFinish) {
    items.push({
      head: 'Best average',
      text: `${s.bestAverageFinish.name} averages ${s.bestAverageFinish.average.toFixed(1)} across the year.`,
    });
  }

  /**
   * ⚠ SIX IS THE COLUMN, AND WHAT IS DROPPED IS SAID.
   *
   * The whole point is a short column beside a long article; twelve items is a
   * second article in a smaller font. But a silent cap is exactly the thing this
   * project refuses — a page showing six of eleven must say eleven — so the
   * count comes back with the items and the modal prints it.
   */
  return items;
}

export const NOTEBOOK_SHOWN = 6;

export interface Notebook {
  items: NoteItem[];
  /** How many there were before the column was cut to length. */
  total: number;
}

export function notebook(facts: PaperFacts): Notebook {
  const all = notebookItems(facts);
  return { items: all.slice(0, NOTEBOOK_SHOWN), total: all.length };
}
